import { Box } from "@material-ui/core";
import { makeStyles, Theme, createStyles } from "@material-ui/core/styles";
import React, { useContext, useEffect, useState } from "react";
import TestRequestExportParameterResponse, {
  IdNameObj,
} from "../../api/models/advanced-search/TestRequestExportParameterResponse";
import TestRequestExportSnapshotResponse from "../../api/models/advanced-search/TestRequestExportSnapshotResponse";
import AdvancedSearchApi from "../../api/services/AdvancedSearchApi";
import CustomButton from "../../components/Button/CustomButton";
import CustomDialog from "../../components/Dialog/CustomDialog";
import InfoBox from "../../components/InfoBox";
import ErrorSnackbarContext from "../../contexts/ErrorSnackbarContext";
import useEnv from "../../hooks/env";
import ExcelExportParameter from "../../models/ExcelExportParameter";
import { tesaBlue, tesaDarkRedError } from "../../styles/colors";
import ExcelResultsTable from "./ExcelResultsTable";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    buttonContainer: {
      alignItems: "center",
      display: "flex",
      flexDirection: "row-reverse",
      justifyContent: "space-between",
      marginBottom: theme.spacing(2),
      marginTop: theme.spacing(2),
    },
    buttonContainerSecondary: {
      position: "relative",
      alignItems: "center",
      display: "flex",
      flexDirection: "row-reverse",
      justifyContent: "flex-start",
    },
    errorText: {
      color: tesaDarkRedError,
    },
    h3: {
      color: theme.palette.primary.main,
      opacity: 0.6,
      marginBottom: theme.spacing(1),
    },
    header: {
      marginBottom: "1rem",
      textTransform: "uppercase",
      "& h2": {
        marginBottom: "0",
      },
    },
    hide: {
      display: "none",
    },
    loading: {
      "& .MuiCircularProgress-root": {
        position: "fixed",
        margin: "auto",
        top: 0,
        left: 0,
        bottom: 0,
        right: 0,
      },
    },
    orText: {
      fontSize: "1rem",
      fontWeight: 600,
    },
    resetButton: { position: "absolute", left: 0 },
    resultCount: {
      color: tesaBlue,
    },
  }),
);

const TITLE = "Export Excel Data";
const INFO = "Please select products, substrates and/or test methods to include results in your excel data.";
const RESULT_COUNT_ERROR = "Exporting with results is only allowed for a result count of 10 test requests or less.";
const PRODUCTS_TESTMETHODS_ERROR = "Product and test method columns require at least one entry.";
const PRODUCTS_ERROR = "Product column requires at least one entry.";
const TESTMETHODS_ERROR = "Test methods column requires at least one entry.";
const SUCCESS_MESSAGE = "Exported successfully.";
const SUCCESS_MESSAGE_WITH_RESULTS = "Exported successfully with results.";

interface ExcelExportDialogProps {
  open: boolean;
  onClose: () => void;
  snapshotObj?: TestRequestExportSnapshotResponse;
  excelConfiguratorTableOpen: boolean;
  setExcelConfiguratorTableOpen: (state: boolean) => void;
  loading: boolean;
  updateLoading: (state: boolean) => void;
}

interface ParameterObject {
  field: ExcelExportParameter;
  value: IdNameObj;
}

const ExcelExportDialog: React.FC<ExcelExportDialogProps> = ({
  open,
  onClose,
  snapshotObj,
  excelConfiguratorTableOpen,
  setExcelConfiguratorTableOpen,
  loading,
  updateLoading,
}) => {
  const classes = useStyles();
  const { api } = useEnv();
  const advancedSearchApi = new AdvancedSearchApi(api);
  const { handleHttpError, showSnackbar } = useContext(ErrorSnackbarContext);
  const [errorMessage, setErrorMessage] = useState("");
  const resultCountErrorCondition = (snapshotObj?.resultCount || 0) > 10;
  const [configuredResults, setConfiguredResults] = useState<TestRequestExportParameterResponse>();
  const [undoQueue, setUndoQueue] = useState<ParameterObject[]>([]);

  const tesaProductsValid = (configuredResults?.tesaProducts?.length || 0) > 0;
  const testMethodsValid = (configuredResults?.testMethods?.length || 0) > 0;

  const updateConfiguredResults = (field: ExcelExportParameter, value: IdNameObj) => {
    const parameterObject: ParameterObject = { field, value };
    const { id, name } = value;

    setUndoQueue((prevState) => [...prevState, parameterObject]);

    setConfiguredResults((prevState) => {
      let substrateClassesState = prevState?.substrateClasses;
      let tesaProductsState = prevState?.tesaProducts;
      let testMethodsState = prevState?.testMethods;

      if (field === ExcelExportParameter.SUBSTRATES) {
        substrateClassesState = prevState?.substrateClasses.filter((substrateClass) => substrateClass.id !== id);
      }
      if (field === ExcelExportParameter.TESA_PRODUCTS) {
        tesaProductsState = prevState?.tesaProducts.filter((tesaProduct) => tesaProduct !== name);
      }
      if (field === ExcelExportParameter.TEST_METHODS) {
        testMethodsState = prevState?.testMethods.filter((testMethod) => testMethod.id !== id);
      }

      return {
        substrateClasses: substrateClassesState as IdNameObj[],
        tesaProducts: tesaProductsState as string[],
        testMethods: testMethodsState as IdNameObj[],
      };
    });
  };

  const getExportSnapshotParameters = () => {
    setExcelConfiguratorTableOpen(true);
    if (snapshotObj?.id) {
      return advancedSearchApi
        .getExportSnapshotParameters(snapshotObj?.id)
        .then((res) => {
          setConfiguredResults(res.data);
        })
        .catch(handleHttpError);
    }
  };

  const handleDownloadWithoutResults = () => {
    updateLoading(true);

    advancedSearchApi
      .exportExcelWithoutResults(snapshotObj?.id || "")
      .then(() => showSnackbar(SUCCESS_MESSAGE, "success"))
      .catch(handleHttpError)
      .finally(() => {
        updateLoading(false);
        onClose();
      });
  };

  const handleDownloadWithResults = () => {
    updateLoading(true);

    const substrateClassIds = configuredResults?.substrateClasses.map((substrate) => substrate.id) || [];
    const tesaProducts = configuredResults?.tesaProducts || [];
    const testMethodIds = configuredResults?.testMethods.map((testMethod) => testMethod.id) || [];

    advancedSearchApi
      .exportExcelWithResults(snapshotObj?.id || "", substrateClassIds, tesaProducts, testMethodIds)
      .then(() => showSnackbar(SUCCESS_MESSAGE_WITH_RESULTS, "success"))
      .catch(handleHttpError)
      .finally(() => {
        updateLoading(false);
        onClose();
      });
  };

  const resetStates = () => {
    setErrorMessage("");
    setUndoQueue([]);
  };

  const resetDialog = () => {
    resetStates();
    getExportSnapshotParameters();
  };

  const validateIncludedResults = () => {
    if (!excelConfiguratorTableOpen) {
      resetStates();
      return;
    }

    if (!tesaProductsValid && !testMethodsValid) {
      setErrorMessage(PRODUCTS_TESTMETHODS_ERROR);
    } else if (!tesaProductsValid) {
      setErrorMessage(PRODUCTS_ERROR);
    } else if (!testMethodsValid) {
      setErrorMessage(TESTMETHODS_ERROR);
    } else {
      setErrorMessage("");
    }
  };

  const handleUndo = () => {
    const lastResult = undoQueue.slice().pop();
    setUndoQueue((prevState) => prevState.filter((item) => item !== lastResult));

    setConfiguredResults((prevState) => {
      const substrateClassesState = prevState?.substrateClasses;
      const tesaProductsState = prevState?.tesaProducts;
      const testMethodsState = prevState?.testMethods;

      if (lastResult?.field === ExcelExportParameter.SUBSTRATES) {
        substrateClassesState?.push(lastResult.value);
      }
      if (lastResult?.field === ExcelExportParameter.TESA_PRODUCTS) {
        tesaProductsState?.push(lastResult.value.name);
      }

      if (lastResult?.field === ExcelExportParameter.TEST_METHODS) {
        testMethodsState?.push(lastResult.value);
      }

      return {
        substrateClasses: substrateClassesState as IdNameObj[],
        tesaProducts: tesaProductsState as string[],
        testMethods: testMethodsState as IdNameObj[],
      };
    });
  };

  useEffect(() => {
    validateIncludedResults();
  }, [open, configuredResults]);

  return (
    <CustomDialog fullWidth maxWidth={excelConfiguratorTableOpen ? "md" : "sm"} open={open} onClose={onClose}>
      <InfoBox className={classes.header} compress text={INFO} TooltipProps={{ placement: "right" }}>
        <h2>{TITLE}</h2>
      </InfoBox>
      {resultCountErrorCondition && <p className={classes.errorText}>{RESULT_COUNT_ERROR}</p>}
      <Box className={classes.buttonContainer}>
        <CustomButton
          download={!loading}
          inProgress={loading}
          primary
          onClick={handleDownloadWithoutResults}
          text="Download Without Results"
          disabled={loading}
        />
        {!excelConfiguratorTableOpen && (
          <>
            <p>OR</p>
            <CustomButton
              disabled={resultCountErrorCondition || loading}
              onClick={getExportSnapshotParameters}
              text={`Configure Results (${snapshotObj?.resultCount})`}
            />
          </>
        )}
      </Box>
      {excelConfiguratorTableOpen && (
        <>
          <h3 className={classes.h3}>Configure Included Results:</h3>
          <ExcelResultsTable configuredResults={configuredResults} updateConfiguredResults={updateConfiguredResults} />
          {errorMessage && (
            <p className={classes.errorText} style={{ textAlign: "right" }}>
              {errorMessage}
            </p>
          )}
          <Box className={classes.buttonContainerSecondary} mt={4}>
            <CustomButton
              download={!loading}
              inProgress={loading}
              disabled={!(tesaProductsValid && testMethodsValid) || loading}
              primary
              onClick={handleDownloadWithResults}
              text={`Download With Results (${snapshotObj?.resultCount})`}
            />
            <Box mr={4} />
            <CustomButton disabled={undoQueue.length === 0 || loading} onClick={handleUndo} text="Undo" />
            <CustomButton
              className={classes.resetButton}
              disabled={undoQueue.length === 0 || loading}
              onClick={resetDialog}
              text="Reset"
            />
          </Box>
        </>
      )}
    </CustomDialog>
  );
};

export default ExcelExportDialog;
